/**
 * 55 — the layer mixer. "Turn the drums off."
 *
 * Two tiers, and the difference between them is the whole design
 * (12_STEMS_AND_LAYERS Part 3):
 *
 *   TIER 1 — mix presets. The Roadie pre-renders each mix as an ordinary MP3
 *     and we point the same <audio> element at a different one. Radio buttons.
 *     Zero memory, zero drift, lock-screen controls keep working, plays on
 *     Trevor's phone at the pub on 3G. THIS IS THE DEFAULT AND IT ALWAYS SHOWS.
 *
 *   TIER 2 — the live layer mixer. Web Audio: every layer decoded to an
 *     AudioBuffer, one AudioBufferSourceNode + one GainNode each, all started
 *     at the same ctx.currentTime + lookahead. Sample-locked by construction.
 *     Real faders, real solo/mute, changeable while playing.
 *
 * Tier 2 is the ONE place in this app that is allowed to touch Web Audio.
 * Everywhere else, playback is an <audio> element, because the iOS silent
 * switch mutes Web Audio and Media Session needs a media element. The mixer
 * accepts that trade knowingly, and pays for it with the ringer-switch hint
 * below.
 *
 * You cannot build a layer mixer out of several <audio> tags. They drift.
 * 20 ms of drift is an audible slapback and 50 ms is the band falling apart.
 *
 * `LayerEngine` is the reusable half: it is also the DAW's playback engine
 * (07_DAW_SPEC). daw.ts imports it from here. Do not fork it.
 */

import { getMix, ApiError, type MixLayer, type MixPreset } from './api';
import * as player from './player';
import { h, clear, fmtTime } from './ui';

/* ------------------------------------------------------------------- gates */

/**
 * A cheap Android decoding five layers is a dead cheap Android. Decoded PCM is
 * 4 bytes x channels x sample-rate x seconds — which is why the Roadie renders
 * layer proxies MONO at 32 kHz / 96 kbps: ~38 MB per five-minute layer instead
 * of the ~105 MB a stereo 44.1k one would cost. Four of those is survivable.
 * Five is not, and neither is a twenty-minute jam.
 */
export const MAX_LAYERS = 4;
export const MAX_DURATION_S = 600; // 10 minutes

/** Enough runway to schedule every source before the clock reaches them. */
const LOOKAHEAD = 0.15;

/** Mute/solo ramp. Long enough not to click, short enough to feel instant. */
const RAMP = 0.015;

const IOS_HINT_KEY = '55.mixer.ios-hint';

/* ================================================================== ENGINE */

/** What the engine needs to know about one layer. */
export interface LayerDef {
  role: string;
  label: string;
  url: string;
  /** Where this layer sits on the take's timeline, in seconds, vs the
   *  reference mix. The Roadie computed this with audalign. We just read it. */
  offset_s: number;
  /** 0..1. The fader. */
  gain?: number;
  muted?: boolean;
}

/** A layer, plus everything the engine knows about it. What a fader draws from. */
export interface Layer extends LayerDef {
  gain: number;
  muted: boolean;
  solo: boolean;
  buffer: AudioBuffer | null;
  src: AudioBufferSourceNode | null;
  gainNode: GainNode | null;
  /** start() has been called on `src`. Calling stop() on a source that was
   *  never started throws InvalidStateError. */
  started: boolean;
}

export interface EngineOptions {
  /** 0..1 across the whole load, plus a line of English to put on screen. */
  onProgress?: (fraction: number, label: string) => void;
  onTime?: (t: number, duration: number) => void;
  onEnded?: () => void;
}

/**
 * Sample-accurate multi-source playback. The whole engine is the scheduling in
 * `schedule()`; everything else is bookkeeping.
 *
 * Position is expressed on the TAKE'S TIMELINE — the same clock the presets
 * use — not on any one layer's buffer. A layer at `offset_s` maps timeline
 * position `p` to buffer position `p - offset_s`. That one line is the entire
 * alignment story on the client; the hard part (finding `offset_s`) already
 * happened on the Mac.
 */
export class LayerEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private layers: Layer[] = [];
  private opts: EngineOptions;

  /**
   * Persistent per-layer FX inserts, for the DAW (07_DAW_SPEC). `in` and `out`
   * survive every re-schedule; the effects sit between them. The scheduled
   * source and its fader are thrown away and rebuilt on every seek, so an FX
   * chain hung off them would be destroyed by the first seek — which is why
   * these two nodes exist and why they are not created per schedule.
   *
   * Empty for the song-screen mixer. The mixer has no effects and wants none.
   */
  private inserts = new Map<string, { in: GainNode; out: GainNode }>();

  /** ctx.currentTime at which the current schedule was started. */
  private startedAt = 0;
  /** timeline position that `startedAt` corresponds to. */
  private seekBase = 0;
  private _playing = false;
  private _duration = 0;
  private raf = 0;
  private abort = new AbortController();
  private destroyed = false;

  constructor(opts: EngineOptions = {}) {
    this.opts = opts;
  }

  get playing(): boolean {
    return this._playing;
  }

  get duration(): number {
    return this._duration;
  }

  /** The layers, for a UI to draw faders from. Treat as read-only. */
  get tracks(): readonly Layer[] {
    return this.layers;
  }

  /** Timeline position, in seconds. */
  get currentTime(): number {
    if (!this.ctx || !this._playing) return this.seekBase;
    const t = this.seekBase + (this.ctx.currentTime - this.startedAt);
    return Math.max(0, Math.min(this._duration, t));
  }

  /**
   * An AudioContext created outside a user gesture starts life 'suspended',
   * which is fine — we resume it inside the tap that calls play(). Creating it
   * here lets us decode while the user is still reading the loading bar.
   */
  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      const Ctor: typeof AudioContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  /** Is there a Web Audio implementation at all? */
  static get supported(): boolean {
    return (
      typeof window !== 'undefined' &&
      (typeof window.AudioContext !== 'undefined' ||
        typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !==
          'undefined')
    );
  }

  /**
   * Fetch every layer and decode it to PCM.
   *
   * Downloads run in parallel (network-bound) and decodes run one at a time
   * (memory-bound) — four simultaneous decodes is exactly the spike that kills
   * the phone we're trying to protect.
   *
   * Silence while this happens is indistinguishable from broken, so every step
   * reports progress.
   */
  async load(defs: LayerDef[]): Promise<void> {
    const ctx = this.ensureCtx();

    this.layers = defs.map((d) => ({
      ...d,
      gain: d.gain ?? 1,
      muted: d.muted ?? false,
      solo: false,
      buffer: null,
      src: null,
      gainNode: null,
      started: false,
    }));

    const n = this.layers.length;
    // Two phases per layer: download is the first 70% of its share, decode the
    // rest. Decoding a 5-minute mono file is not instant on a phone.
    const dl = new Array<number>(n).fill(0);
    const dec = new Array<number>(n).fill(0);
    const report = (label: string) => {
      let sum = 0;
      for (let i = 0; i < n; i++) sum += (dl[i] ?? 0) * 0.7 + (dec[i] ?? 0) * 0.3;
      this.opts.onProgress?.(n ? sum / n : 1, label);
    };

    report('Fetching layers…');

    const bodies = await Promise.all(
      this.layers.map(async (L, i) => {
        const buf = await fetchWithProgress(L.url, this.abort.signal, (f) => {
          dl[i] = f;
          report('Fetching layers…');
        });
        dl[i] = 1;
        report('Fetching layers…');
        return buf;
      }),
    );

    if (this.destroyed) return;

    for (let i = 0; i < n; i++) {
      const L = this.layers[i];
      const body = bodies[i];
      if (!L || !body) continue;
      report(`Unpacking ${L.label}…`);
      L.buffer = await decode(ctx, body);
      dec[i] = 1;
      report(`Unpacking ${L.label}…`);
      if (this.destroyed) return;
    }

    // The take is as long as its longest layer, measured on the timeline.
    let d = 0;
    for (const L of this.layers) {
      if (L.buffer) d = Math.max(d, L.offset_s + L.buffer.duration);
    }
    this._duration = d;
    report('Ready');
  }

  /**
   * The whole engine, essentially.
   *
   * Every source is started against ONE reading of ctx.currentTime, so they are
   * locked to each other by construction — not synced, locked. There is no
   * drift to correct because there is no second clock.
   */
  private schedule(seek: number): void {
    const ctx = this.ensureCtx();
    const master = this.master;
    if (!master) return;

    const t0 = ctx.currentTime + LOOKAHEAD;

    for (const L of this.layers) {
      if (!L.buffer) continue;

      const src = ctx.createBufferSource();
      src.buffer = L.buffer;
      const g = ctx.createGain();
      g.gain.value = this.effectiveGain(L);
      g.connect(master);

      // With an FX insert (the DAW): src -> in -> [effects] -> out -> fader.
      // Without one (the mixer): src -> fader. Same fader either way, so mute,
      // solo and gain behave identically whether or not there are effects.
      const ins = this.inserts.get(L.role);
      if (ins) {
        src.connect(ins.in);
        ins.out.connect(g);
      } else {
        src.connect(g);
      }

      L.src = src;
      L.gainNode = g;
      L.started = false;

      // Timeline -> buffer. This is the offset correction, and it is the only
      // place alignment is applied.
      const bufAt = seek - L.offset_s;

      if (bufAt >= L.buffer.duration) {
        // This layer has already finished by the time we're seeking to. Leave
        // it wired up (so its fader still works) but never start it.
        continue;
      }

      if (bufAt >= 0) {
        src.start(t0, bufAt);
      } else {
        // We're before this layer's first sample. It must start LATER, by
        // exactly the amount it is late — NOT at buffer position 0 right now,
        // which is the clamp the naive version does and it flams by seconds on
        // a real "-brad only" render.
        src.start(t0 - bufAt, 0);
      }
      L.started = true;
    }

    this.startedAt = t0;
    this.seekBase = seek;
  }

  /** Tear down the current schedule. Sources are one-shot; we rebuild on seek. */
  private teardown(): void {
    for (const L of this.layers) {
      if (L.src) {
        try {
          if (L.started) L.src.stop();
        } catch {
          /* already ended — fine */
        }
        L.src.disconnect();
      }
      L.src = null;
      // The GainNode is disposable too; a fresh schedule makes fresh ones.
      L.gainNode?.disconnect();
      L.gainNode = null;
      L.started = false;
    }
    // The insert PAIR survives (the effects hang off it), but its output was
    // wired to a fader that is now gone. Unwire it, or the next schedule leaves
    // a dangling connection and the layer plays through two faders at once.
    for (const ins of this.inserts.values()) ins.out.disconnect();
  }

  /** Must be called from inside a user gesture the first time, or iOS won't. */
  async play(from?: number): Promise<void> {
    if (this.destroyed) return;
    const ctx = this.ensureCtx();
    const seek = from ?? this.currentTime;

    try {
      await ctx.resume();
    } catch {
      /* a suspended context that won't resume shows up as silence, and the
         ringer hint below is the thing that actually fixes that */
    }
    if (this.destroyed) return;

    this.teardown();
    this.schedule(Math.max(0, Math.min(seek, this._duration)));
    this._playing = true;
    this.startTicking();
  }

  pause(): void {
    if (!this._playing) return;
    const at = this.currentTime;
    this.teardown();
    this._playing = false;
    this.seekBase = at;
    this.stopTicking();
    this.opts.onTime?.(at, this._duration);
  }

  /** Stop everything, rebuild, restart at a new t0. Sample-locked again. */
  seek(t: number): void {
    const target = Math.max(0, Math.min(t, this._duration));
    if (this._playing) {
      void this.play(target);
    } else {
      this.seekBase = target;
      this.opts.onTime?.(target, this._duration);
    }
  }

  /* --------------------------------------------------------------- faders */

  /**
   * Mute and solo are gain ramps. NEVER disconnect() — disconnecting a live
   * source mid-sample is a click, and a mixer that clicks every time you tap
   * something feels broken even though it isn't.
   */
  private effectiveGain(L: Layer): number {
    const anySolo = this.layers.some((x) => x.solo);
    if (anySolo) return L.solo ? L.gain : 0;
    return L.muted ? 0 : L.gain;
  }

  private applyGains(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const L of this.layers) {
      L.gainNode?.gain.setTargetAtTime(this.effectiveGain(L), ctx.currentTime, RAMP);
    }
  }

  setGain(role: string, gain: number): void {
    const L = this.layers.find((x) => x.role === role);
    if (!L) return;
    L.gain = Math.max(0, Math.min(1, gain));
    this.applyGains();
  }

  setMuted(role: string, muted: boolean): void {
    const L = this.layers.find((x) => x.role === role);
    if (!L) return;
    L.muted = muted;
    this.applyGains();
  }

  setSolo(role: string, solo: boolean): void {
    const L = this.layers.find((x) => x.role === role);
    if (!L) return;
    L.solo = solo;
    this.applyGains();
  }

  /* ======================================== the DAW's half (07_DAW_SPEC) ==
   *
   * Everything below is additive. The song-screen mixer above does not call any
   * of it, and its behaviour is unchanged. The DAW needs four things the mixer
   * never does: a context to record and decode against, a way to drop a
   * just-recorded buffer onto the timeline WITHOUT re-downloading the layers it
   * already has, a stable place to hang effects, and a way to nudge an offset.
   *
   * These live here, in the one engine, rather than in a second one — a second
   * engine is how you end up with two clocks, and two clocks is how you end up
   * with a flam.
   */

  /** The engine's context. The DAW records, decodes and counts in against it. */
  get context(): AudioContext {
    return this.ensureCtx();
  }

  /** The master bus. A count-in click connects here so it is heard, not recorded. */
  get output(): GainNode {
    this.ensureCtx();
    return this.master!;
  }

  /**
   * Put an already-decoded buffer on the timeline. This is how an overdub
   * becomes audible the moment the take ends: it is stacked on the jam before
   * the upload has even begun, which is the entire point of the feature.
   *
   * If we're rolling, the new source is scheduled against the SAME
   * timeline→context mapping the others were started with (startedAt/seekBase),
   * never against a fresh clock reading. A fresh reading is a flam.
   */
  addLayer(def: LayerDef, buffer: AudioBuffer): void {
    const ctx = this.ensureCtx();
    const master = this.master;
    if (!master) return;

    const L: Layer = {
      ...def,
      gain: def.gain ?? 1,
      muted: def.muted ?? false,
      solo: false,
      buffer,
      src: null,
      gainNode: null,
      started: false,
    };
    this.layers.push(L);
    this.recomputeDuration();

    if (!this._playing) return;

    const g = ctx.createGain();
    g.gain.value = this.effectiveGain(L);
    g.connect(master);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const ins = this.inserts.get(L.role);
    if (ins) {
      src.connect(ins.in);
      ins.out.connect(g);
    } else {
      src.connect(g);
    }
    L.src = src;
    L.gainNode = g;

    // ctx time at which timeline position p occurs, under the schedule that is
    // ALREADY running: startedAt + (p - seekBase).
    const ctxAtStart = this.startedAt + (L.offset_s - this.seekBase);
    const earliest = ctx.currentTime + LOOKAHEAD;

    if (ctxAtStart >= earliest) {
      src.start(ctxAtStart, 0);
      L.started = true;
    } else {
      const bufAt = this.seekBase + (earliest - this.startedAt) - L.offset_s;
      if (bufAt < buffer.duration) {
        src.start(earliest, Math.max(0, bufAt));
        L.started = true;
      }
    }
  }

  /** Throw an overdub away. Nothing is deleted on the server; this is local. */
  removeLayer(role: string): void {
    const i = this.layers.findIndex((x) => x.role === role);
    if (i < 0) return;
    const L = this.layers[i];
    if (L) {
      if (L.src) {
        try {
          if (L.started) L.src.stop();
        } catch {
          /* already ended */
        }
        L.src.disconnect();
      }
      L.gainNode?.disconnect();
      L.buffer = null;
    }
    this.layers.splice(i, 1);
    const ins = this.inserts.get(role);
    if (ins) {
      ins.in.disconnect();
      ins.out.disconnect();
      this.inserts.delete(role);
    }
    this.recomputeDuration();
  }

  /**
   * Move a layer in time.
   *
   * The Roadie's `offset_s` is right for anything it aligned with audalign. But
   * a browser overdub carries round-trip latency nobody can measure exactly, so
   * the DAW gives the human a nudge — they are the one who can hear the flam.
   */
  setOffset(role: string, offset_s: number): void {
    const L = this.layers.find((x) => x.role === role);
    if (!L) return;
    L.offset_s = offset_s;
    this.recomputeDuration();
    if (this._playing) void this.play(this.currentTime);
  }

  /**
   * A stable insert point for this layer's effects: `src -> in -> [fx] -> out
   * -> fader`. Created dry (in straight to out) and it stays wired across every
   * seek, so a reverb survives a rewind.
   */
  insert(role: string): { in: GainNode; out: GainNode } {
    const existing = this.inserts.get(role);
    if (existing) return existing;

    const ctx = this.ensureCtx();
    const pair = { in: ctx.createGain(), out: ctx.createGain() };
    pair.in.connect(pair.out); // dry until something is put in the way
    this.inserts.set(role, pair);

    // Rolling? Then the source is already connected straight to the fader.
    // Re-schedule so it goes through the insert instead.
    if (this._playing) void this.play(this.currentTime);
    return pair;
  }

  private recomputeDuration(): void {
    let d = 0;
    for (const L of this.layers) {
      if (L.buffer) d = Math.max(d, L.offset_s + L.buffer.duration);
    }
    this._duration = d;
  }

  /* ---------------------------------------------------------------- clock */

  private startTicking(): void {
    if (this.raf) return;
    const loop = () => {
      if (!this._playing) {
        this.raf = 0;
        return;
      }
      const t = this.currentTime;
      this.opts.onTime?.(t, this._duration);
      if (this._duration > 0 && t >= this._duration - 0.02) {
        this.pause();
        this.seekBase = 0;
        this.opts.onEnded?.();
        return;
      }
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private stopTicking(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Free ~150 MB of decoded PCM. Call it. */
  destroy(): void {
    this.destroyed = true;
    this.abort.abort();
    this.stopTicking();
    this.teardown();
    this._playing = false;
    for (const L of this.layers) L.buffer = null;
    this.layers = [];
    for (const ins of this.inserts.values()) {
      ins.in.disconnect();
      ins.out.disconnect();
    }
    this.inserts.clear();
    this.master?.disconnect();
    this.master = null;
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
  }
}

/* ------------------------------------------------------------------ decode */

/**
 * Decode a blob of bytes on the engine's own context. The DAW uses it on a
 * just-recorded overdub, so the recording lands on the same clock — and at the
 * same sample rate — as everything already on the timeline.
 */
export function decodeAudio(ctx: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
  return decode(ctx, data);
}

/** Safari only grew the promise form of decodeAudioData late. Support both. */
function decode(ctx: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    const p = ctx.decodeAudioData(data, resolve, reject) as unknown;
    if (p && typeof (p as Promise<AudioBuffer>).then === 'function') {
      (p as Promise<AudioBuffer>).then(resolve, reject);
    }
  });
}

/**
 * fetch(), but it tells you how far along it is.
 *
 * R2's CORS is configured to expose Content-Length (and ETag, Content-Range,
 * Accept-Ranges), which is what makes a real progress bar possible here rather
 * than a fake one that crawls to 90% and lies.
 */
async function fetchWithProgress(
  url: string,
  signal: AbortSignal,
  onProgress: (fraction: number) => void,
): Promise<ArrayBuffer> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error('layer fetch failed');

  const total = Number(res.headers.get('content-length') ?? 0);
  if (!res.body || !total) {
    // No stream or no length: we can't measure. Don't lie with a fake bar —
    // just land on done when it's done.
    const buf = await res.arrayBuffer();
    onProgress(1);
    return buf;
  }

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.byteLength;
      onProgress(Math.min(1, received / total));
    }
  }

  const out = new Uint8Array(received);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  onProgress(1);
  return out.buffer;
}

/* =================================================================== THE UI */

function isIOS(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ lies and calls itself a Mac. The touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * Mount the mixer for one take.
 *
 * Tier 1 renders immediately and always. Tier 2 is offered only when it will
 * actually work, and when it isn't offered we say why in words a person can
 * act on — a greyed-out control that won't say what's wrong is just rude.
 *
 * Returns its own cleanup. Nothing here touches routing.
 */
export function mountMixer(root: HTMLElement, takeId: string, audio: HTMLAudioElement): () => void {
  const abort = new AbortController();
  let engine: LayerEngine | null = null;
  let disposed = false;

  /** Which Tier-1 preset the <audio> is currently pointed at. */
  let presetId: string | null = null;
  let presets: MixPreset[] = [];

  const wrap = h('section', { class: 'mixer', 'aria-label': 'Mix' });
  root.appendChild(wrap);

  /* ------------------------------------------------------------- teardown */

  const closeEngine = (restoreTo?: number) => {
    if (!engine) return;
    const at = restoreTo ?? engine.currentTime;
    engine.destroy();
    engine = null;

    // Hand the position back to the <audio> element on the same timeline the
    // presets use: audio.currentTime = timeline - preset.offset_s.
    const p = presets.find((x) => x.id === presetId);
    const back = Math.max(0, at - (p?.offset_s ?? 0));
    try {
      audio.currentTime = back;
    } catch {
      /* not loaded yet; the transport will land it */
    }
  };

  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    abort.abort();
    audio.removeEventListener('play', onAudioPlay);
    closeEngine();
    wrap.remove();
  };

  /**
   * The transport and the mixer cannot both make noise. If the user hits play
   * on the transport (or the lock screen) while the mixer is running, the
   * transport wins and the mixer folds — that's the less surprising of the two,
   * because they just pressed a play button and expect to hear the song.
   */
  function onAudioPlay() {
    if (engine) {
      closeEngine();
      renderTier2();
    }
  }
  audio.addEventListener('play', onAudioPlay);

  /* ------------------------------------------------------------- tier one */

  const tier1 = h('div', { class: 'presets', role: 'radiogroup', 'aria-label': 'Mix' });
  const tier2 = h('div', { class: 'mixer-t2' });

  function renderTier1() {
    clear(tier1);
    for (const p of presets) {
      const on = p.id === presetId;
      const btn = h('button', { class: 'preset', role: 'radio', 'aria-checked': on ? 'true' : 'false' }, [
        h('span', { class: 'radio', 'aria-hidden': 'true' }),
        h('span', { class: 'preset-name', text: p.name }),
        h('span', { class: 'preset-len', text: fmtTime(p.duration_s) }),
      ]);
      btn.addEventListener('click', () => void choosePreset(p));
      tier1.appendChild(btn);
    }
  }

  /**
   * Switching a preset preserves position across a different bounce:
   *   new.currentTime = old.currentTime + (offset_new - offset_old)
   * so flipping to brad-only at the chorus lands on the chorus, not somewhere
   * in the second verse. player.switchPreset() owns that maths and the 20 ms
   * crossfade; we do not reimplement it.
   */
  async function choosePreset(p: MixPreset) {
    if (p.id === presetId && !engine) return;

    // Leaving the live mixer? Give the <audio> the position back first.
    if (engine) closeEngine();

    const old = presets.find((x) => x.id === presetId);
    presetId = p.id;
    renderTier1();
    renderTier2();

    if (audio === player.element()) {
      await player.switchPreset(p.id, p.url, p.offset_s, old?.offset_s ?? 0);
    } else {
      // A detached <audio> (the DAW's, a preview): same maths, done locally.
      const wasPlaying = !audio.paused;
      const target = Math.max(0, audio.currentTime + (p.offset_s - (old?.offset_s ?? 0)));
      audio.src = p.url;
      audio.load();
      const restore = () => {
        try {
          audio.currentTime = target;
        } catch {
          /* ignore */
        }
      };
      if (audio.readyState >= 1) restore();
      else audio.addEventListener('loadedmetadata', restore, { once: true });
      if (wasPlaying) await audio.play().catch(() => {});
    }
  }

  /* ------------------------------------------------------------- tier two */

  let layers: MixLayer[] = [];
  let mixerReady = false;
  /** The take's length, from the presets. 0 = we don't know yet. */
  let durationS = 0;

  const tooManyLayers = () => layers.length > MAX_LAYERS;
  const tooLong = () => durationS > MAX_DURATION_S;

  function renderTier2() {
    clear(tier2);

    if (layers.length === 0) return;

    // The alignment gate. A mixer that flams is worse than no mixer, so until
    // the Roadie has computed offset_s for every layer there is no mixer here
    // at all — no faders, no engine, nothing to press.
    if (!mixerReady && !tooManyLayers() && !tooLong()) {
      tier2.appendChild(
        h('p', {
          class: 'mixer-note',
          text: 'Still lining these layers up. The mixer turns up when they’re ready.',
        }),
      );
      return;
    }

    // The phone gate. Say it in words. Do not just grey something out.
    if (tooManyLayers() || tooLong()) {
      const why = tooManyLayers()
        ? `There are ${layers.length} layers in this one.`
        : 'This one runs over ten minutes.';
      tier2.appendChild(
        h('p', {
          class: 'mixer-note',
          text: `${why} That’s more than a phone can hold at once — open this on a computer for the full mixer. The mixes above work everywhere.`,
        }),
      );
      return;
    }

    if (!LayerEngine.supported) {
      tier2.appendChild(
        h('p', {
          class: 'mixer-note',
          text: 'This browser can’t run the live mixer. The mixes above work everywhere.',
        }),
      );
      return;
    }

    if (!engine) {
      const open = h('button', { class: 'btn-sm primary mixer-open', text: 'Open the layer mixer' });
      open.addEventListener('click', () => void openMixer());
      tier2.appendChild(open);
      return;
    }

    renderFaders();
  }

  /* the loading bar — silence while decoding is indistinguishable from broken */
  function renderLoading(): (fraction: number, label: string) => void {
    clear(tier2);
    const bar = h('div', { class: 'bar' });
    const label = h('p', { class: 'mixer-note', text: 'Loading layers…' });
    tier2.append(
      label,
      h('div', { class: 'mixer-load', role: 'progressbar', 'aria-label': 'Loading layers' }, [bar]),
    );
    return (fraction, text) => {
      bar.style.width = `${Math.round(fraction * 100)}%`;
      label.textContent = text;
    };
  }

  async function openMixer() {
    // A real tap. This is the user gesture Web Audio needs on iOS, and we must
    // not lose it behind an await.
    showIOSHintOnce();

    // The <audio> and the engine cannot both be making noise.
    const startAt = audio.currentTime + (presets.find((x) => x.id === presetId)?.offset_s ?? 0);
    audio.pause();

    const onProgress = renderLoading();
    const e = new LayerEngine({
      onProgress,
      onEnded: () => renderFaders(),
      onTime: () => updateClock(),
    });
    engine = e;

    try {
      await e.load(
        layers.map((l) => ({
          role: l.role,
          label: l.label,
          url: l.url,
          offset_s: l.offset_s,
          gain: 1,
          muted: !l.default_on,
        })),
      );
    } catch {
      // Torn down mid-load (closed the mixer, left the screen) — the abort is
      // an expected outcome, not a failure to report.
      if (disposed || engine !== e) return;
      engine = null;
      e.destroy();
      clear(tier2);
      tier2.appendChild(
        h('p', {
          class: 'mixer-note',
          text: 'Couldn’t load the layers. The mixes above still work.',
        }),
      );
      return;
    }

    if (disposed || engine !== e) return;

    renderFaders();
    await e.play(Math.max(0, startAt));
    renderFaders();
  }

  let clockEl: HTMLElement | null = null;
  function updateClock() {
    if (!engine || !clockEl) return;
    clockEl.textContent = `${fmtTime(engine.currentTime)} / ${fmtTime(engine.duration)}`;
  }

  function renderFaders() {
    const e = engine;
    if (!e) return;
    clear(tier2);

    const head = h('div', { class: 'mixer-head' }, [
      h('h4', { text: 'Layers' }),
      (clockEl = h('span', { class: 'mixer-clock', text: '0:00 / 0:00' })),
    ]);

    const transport = h('button', {
      class: 'btn-sm primary',
      text: e.playing ? 'Pause' : 'Play',
    });
    transport.addEventListener('click', () => {
      if (!engine) return;
      if (engine.playing) engine.pause();
      else void engine.play();
      renderFaders();
    });

    const close = h('button', { class: 'btn-sm', text: 'Close mixer' });
    close.addEventListener('click', () => {
      closeEngine();
      renderTier2();
    });

    const rows = h('div', { class: 'layers' });
    for (const L of e.tracks) {
      const mute = h('button', {
        class: 'layer-btn',
        'aria-pressed': L.muted ? 'true' : 'false',
        'aria-label': `Mute ${L.label}`,
        text: 'M',
      });
      const solo = h('button', {
        class: 'layer-btn solo',
        'aria-pressed': L.solo ? 'true' : 'false',
        'aria-label': `Solo ${L.label}`,
        text: 'S',
      });
      const fader = h('input', {
        type: 'range',
        class: 'fader',
        min: '0',
        max: '1',
        step: '0.01',
        value: String(L.gain),
        'aria-label': `${L.label} level`,
      });

      const role = L.role;
      mute.addEventListener('click', () => {
        if (!engine) return;
        // `tracks` hands back the live objects, and setMuted mutates them.
        // Work out the next value BEFORE the call, or the aria state inverts.
        const next = !(engine.tracks.find((x) => x.role === role)?.muted ?? false);
        engine.setMuted(role, next);
        mute.setAttribute('aria-pressed', next ? 'true' : 'false');
      });
      solo.addEventListener('click', () => {
        if (!engine) return;
        const next = !(engine.tracks.find((x) => x.role === role)?.solo ?? false);
        engine.setSolo(role, next);
        // Solo changes what every other row is doing, so repaint the lot.
        renderFaders();
      });
      fader.addEventListener('input', () => {
        engine?.setGain(role, Number(fader.value));
      });

      rows.appendChild(
        h('div', { class: 'layer' }, [
          h('span', { class: 'layer-name', text: L.label }),
          mute,
          solo,
          fader,
        ]),
      );
    }

    tier2.append(head, rows, h('div', { class: 'mixer-actions' }, [close, transport]));
    updateClock();
  }

  /**
   * The one sentence that stops the angry text from Rob.
   *
   * Web Audio — unlike <audio> — is muted by the iOS ringer switch. He flips it
   * off in the pub, opens the mixer, hears nothing, and concludes the app is
   * broken. Once per device is enough.
   */
  function showIOSHintOnce() {
    if (!isIOS()) return;
    try {
      if (localStorage.getItem(IOS_HINT_KEY)) return;
      localStorage.setItem(IOS_HINT_KEY, '1');
    } catch {
      /* private mode: show it, it's one line */
    }
    const hint = h('p', {
      class: 'mixer-hint',
      role: 'status',
      text: 'If you hear nothing, flip the ringer switch on the side of your phone.',
    });
    wrap.insertBefore(hint, wrap.firstChild);
    setTimeout(() => hint.remove(), 9000);
  }

  /* ---------------------------------------------------------------- fetch */

  void (async () => {
    try {
      const mix = await getMix(takeId, abort.signal);
      if (disposed) return;

      presets = mix.presets;
      layers = mix.layers;
      mixerReady = mix.mixer_ready;
      durationS = presets.reduce((m, p) => Math.max(m, p.duration_s ?? 0), 0);

      // If the transport is already on a preset, start there; otherwise the
      // first one is the full mix.
      const now = player.get().now;
      presetId =
        (now && audio === player.element() ? now.presetId : null) ?? presets[0]?.id ?? null;

      // One preset is not a choice, it's a label. Only show radio buttons when
      // there is something to choose between.
      if (presets.length > 1) {
        wrap.appendChild(tier1);
        renderTier1();
      }
      wrap.appendChild(tier2);
      renderTier2();

      // Nothing to offer at all: say nothing. An empty box is worse than no box.
      if (presets.length <= 1 && layers.length === 0) wrap.remove();
    } catch (e) {
      if (disposed) return;
      if (e instanceof ApiError) {
        // No mixes is not an error worth shouting about. The song still plays.
        wrap.remove();
        return;
      }
      wrap.remove();
    }
  })();

  return cleanup;
}
